import React from 'react';
import PropTypes from 'prop-types';
import { Table, TableBody, TableCell, TableHead, TableRow, Typography, CircularProgress } from '@material-ui/core';

import viewDoc from 'core/assets/svg/view-task.svg';
import removeIcon from 'core/assets/svg/remove.svg';
import { useToast } from 'core/components/toast';
import { dateTimeFormatter } from 'core/helpers/formatters';
import { IconButtonWithTooltip } from 'core/components/icon-button-with-tooltip';
import { downloadTaskDocument } from 'customer/helpers/policy-document-service';

import useStyles from './document-list.styles';

const DocumentList = ({ documents, onDelete, loading }) => {
  const classes = useStyles();

  const toast = useToast();

  const handleViewDoc = async (document) => {
    try {
      await downloadTaskDocument(document.documentUrl || document.id);
    } catch {
      toast.notify({
        type: 'error',
        message: 'Failed to download document'
      });
    }
  };

  const handleDelete = async (documentId) => {
    if (!onDelete) {
      return;
    }
    try {
      await onDelete(documentId);
      toast.notify({
        type: 'success',
        message: 'Document removed'
      });
    } catch {
      toast.notify({
        type: 'error',
        message: 'Failed to remove document'
      });
    }
  };

  const formatDate = (dateString) => {
    try {
      return dateTimeFormatter(new Date(dateString));
    } catch {
      return 'Invalid date';
    }
  };

  if (loading) {
    return (
      <div className={classes.loadingContainer}>
        <CircularProgress size={24} color="secondary" />
        <Typography variant="body2" className={classes.loadingText}>
          Loading documents...
        </Typography>
      </div>
    );
  }

  if (!documents || documents.length === 0) {
    return (
      <div className={classes.emptyState}>
        <Typography variant="body2" className={classes.emptyText}>
          No documents have been uploaded to this task yet
        </Typography>
      </div>
    );
  }

  return (
    <Table className={classes.table}>
      <TableHead>
        <TableRow>
          <TableCell className={classes.headerCell}>FILE NAME</TableCell>
          <TableCell className={classes.headerCell}>UPLOADED</TableCell>
          {/* need the following empty tablecell header for the view or copy action buttons */}
          <TableCell />
        </TableRow>
      </TableHead>
      <TableBody>
        {documents.map((document) => {
          const documentId = document.id || document.path || document.documentUrl;
          return (
            <TableRow key={documentId} className={classes.tableRow}>
              <TableCell className={classes.fileNameCell}>
                <div className={classes.fileNameContainer}>
                  <span className={classes.fileName}>{document.fileName}</span>
                </div>
              </TableCell>
              <TableCell className={classes.cell}>
                <div className={classes.uploadInfo}>
                  <div>{formatDate(document.uploadedAt)}</div>
                </div>
              </TableCell>
              <TableCell className={classes.actionsCell}>
                <div className={classes.actions}>
                  <IconButtonWithTooltip
                    size="small"
                    tooltip="View file"
                    icon={viewDoc}
                    onClick={() => handleViewDoc(document)}
                    alt="view file"
                    iconClassName={classes.icon}
                  />
                  <IconButtonWithTooltip
                    size="small"
                    tooltip="Remove file"
                    icon={removeIcon}
                    onClick={() => handleDelete(documentId)}
                    alt="delete file"
                    iconClassName={classes.icon}
                  />
                </div>
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
};

DocumentList.propTypes = {
  documents: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      documentUrl: PropTypes.string,
      path: PropTypes.string,
      fileName: PropTypes.string.isRequired,
      uploadedAt: PropTypes.string
    })
  ),
  onDelete: PropTypes.func,
  loading: PropTypes.bool
};

DocumentList.defaultProps = {
  documents: [],
  loading: false,
  onDelete: undefined
};

export default DocumentList;
